// Desktop (xl, 1280px+) layouts of the "Sarees under ₹2,000" banner. The phone/tablet layout lives
// in SareesUnderBanner.jsx; this file only draws the wide versions.
//
// THREE DESIGNS, ONE PER STORE
// ----------------------------
// Satish sir approved three desktop designs (27 Oct) and asked to "cycle them across different
// storefronts". Each store gets ONE of them, picked from its store id (storeId % 3), not a fresh
// random pick per visit: a shopper never sees the banner change between reloads, and because store
// ids are sequential the three designs spread evenly across stores.
//
//   warm     — maroon ground, thin gold frame, big gold price, framed cards with price tags
//   arches   — saffron ground, temple-arch photo frames, round "Under ₹2,000" stamp
//   backdrop — a blurred saree photo fills the banner, three cards fanned out on the right
//
// The photos and prices are the same in-stock sarees the banner already fetched; nothing extra is
// requested. Only one of these renders, inside a `hidden xl:flex` wrapper, so on a phone its lazy
// images are never loaded.

import React from "react";
import { Trans } from "react-i18next";
import { Imagekit, ikSrc } from "../../Pages/CommonPages/ImageKit";
import { formatCardAmount } from "../../Utils/cardPrice";

export const DESKTOP_DESIGNS = ["warm", "arches", "backdrop"];

// Small, stable string hash for the rare store with no id in its details payload.
const hash = (s) =>
  [...String(s)].reduce((h, c) => (h * 31 + c.charCodeAt(0)) >>> 0, 7);

/** The desktop design for a store: same store, same design, every visit. */
export function pickDesktopDesign(storeId, storeName = "") {
  const id = Number(storeId);
  const n = Number.isFinite(id) && id > 0 ? id : hash(storeName || "store");
  return DESKTOP_DESIGNS[n % DESKTOP_DESIGNS.length];
}

// Card price: the same amount the product cards show (after the store discount), plus the MRP
// to strike through when it is higher (same rule as the listing card).
const cardPrice = (p) => {
  const d = p?.priceDetails || {};
  const amount = d.finalAmount ?? d.sellingPrice ?? d.mrp;
  if (amount == null) return null;
  return {
    label: formatCardAmount(amount),
    mrp: d.mrp && d.mrp > amount ? formatCardAmount(d.mrp) : null,
  };
};

const photoOf = (p) => p.productThumbnail || p.productUrl;

const Arrow = () => (
  <svg
    viewBox="0 0 24 24"
    width="15"
    height="15"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M5 12h14m-6-6 6 6-6 6" />
  </svg>
);

const PriceTag = ({ p, className = "left-[8px]" }) => {
  const price = cardPrice(p);
  if (!price) return null;
  return (
    <span
      className={`absolute bottom-[8px] ${className} bg-white text-ink-900 rounded-full text-[12.5px] font-[500] px-[10px] py-[4px] shadow-[0_2px_6px_rgba(0,0,0,0.15)] whitespace-nowrap`}
    >
      {price.mrp && (
        <span className="line-through text-[10.5px] font-normal text-ink-500 mr-[4px]">
          {price.mrp}
        </span>
      )}
      {price.label}
    </span>
  );
};

const Photo = ({ p }) => (
  <Imagekit
    IMGName={photoOf(p)}
    alt=""
    width={400}
    sizes="200px"
    className="absolute inset-0 !w-full !h-full !object-cover !object-top"
  />
);

// Heading with the price as its own styled line. A <Trans> placeholder rather than two strings,
// because Hindi puts the price FIRST ("₹2,000 से कम की साड़ियाँ").
const Heading = ({ price, className, priceClassName }) => (
  <h2 className={`m-0 font-[500] ${className}`}>
    <Trans
      i18nKey="sareesUnder.headingRich"
      values={{ price }}
      components={[<span className={`block ${priceClassName}`} />]}
    />
  </h2>
);

function Warm({ photos, price, eyebrow, sub, cta }) {
  return (
    <div className="relative flex items-center gap-[28px] h-[300px] px-[36px] bg-[#5E1B2A] text-[#FBEFE3]">
      <div
        className="absolute inset-[14px] rounded-[10px] border border-[rgba(242,181,68,0.35)] pointer-events-none"
        aria-hidden="true"
      />
      <div className="relative flex-1 min-w-0 flex flex-col gap-[10px]">
        <span className="text-[12px] tracking-[0.14em] font-[500] text-[#F2B544]">{eyebrow}</span>
        <Heading
          price={price}
          className="text-[26px] leading-[1.15]"
          priceClassName="mt-[4px] text-[64px] leading-none tracking-[-1.5px] text-[#F2B544]"
        />
        <span className="text-[14px] text-[#E8D5C8]">{sub}</span>
        <span className="mt-[8px] inline-flex items-center gap-[6px] w-max rounded-full px-[20px] py-[10px] text-[14px] font-[500] bg-[#F2B544] text-[#5E1B2A]">
          {cta} <Arrow />
        </span>
      </div>
      <div className="relative flex gap-[16px]" aria-hidden="true">
        {photos.map((p, i) => (
          <div
            key={p.skuId || p.productId}
            className={`relative w-[168px] h-[224px] rounded-[12px] overflow-hidden border-[3px] border-[#FBEFE3] shadow-[0_10px_24px_rgba(0,0,0,0.28)] ${
              i === 1 ? "-translate-y-[10px]" : ""
            }`}
          >
            <Photo p={p} />
            <PriceTag p={p} />
          </div>
        ))}
      </div>
    </div>
  );
}

function Arches({ photos, price, eyebrow, sub, cta }) {
  return (
    <div className="relative flex items-center gap-[26px] h-[310px] px-[40px] bg-[#E9A23B] text-[#3A1D08]">
      <div className="flex-1 min-w-0 flex flex-col gap-[10px]">
        <span className="text-[12px] tracking-[0.14em] font-[500] text-[#7A3B0C]">{eyebrow}</span>
        <Heading price={price} className="text-[40px] leading-[1.08]" priceClassName="" />
        <span className="text-[15px] text-[#5A2D0C]">{sub}</span>
        <span className="mt-[6px] inline-flex items-center gap-[6px] w-max rounded-full px-[20px] py-[10px] text-[14px] font-[500] bg-[#3A1D08] text-[#FBEFE3]">
          {cta} <Arrow />
        </span>
      </div>
      <div className="relative flex items-end gap-[16px] h-full pt-[30px]" aria-hidden="true">
        {/* Round stamp on the last arch's corner: well clear of the copy, inside the banner. */}
        <div className="absolute right-[-24px] top-[14px] z-[2] w-[104px] h-[104px] rounded-full bg-[#7A1F2B] text-[#FBE3B8] border-2 border-dashed border-[#FBE3B8] shadow-[0_6px_16px_rgba(0,0,0,0.2)] flex flex-col items-center justify-center leading-[1.1] font-[500] -rotate-12">
          <Trans
            i18nKey="sareesUnder.stamp"
            values={{ price }}
            components={[
              <small className="text-[11px] tracking-[0.12em]" />,
              <b className="text-[22px] font-[500]" />,
            ]}
          />
        </div>
        {photos.map((p) => (
          <div
            key={p.skuId || p.productId}
            className="relative w-[160px] h-[250px] rounded-t-[80px] overflow-hidden border-4 border-b-0 border-[#FBEFE3]"
          >
            <Photo p={p} />
            <PriceTag p={p} className="left-1/2 -translate-x-1/2 bottom-[14px]" />
          </div>
        ))}
      </div>
    </div>
  );
}

// Fan positions by photo count (left px, rotation). The front card is the middle one.
const FAN = {
  1: [{ left: 125, top: 22, rot: 0 }],
  2: [
    { left: 60, top: 22, rot: -6 },
    { left: 200, top: 22, rot: 6 },
  ],
  3: [
    { left: 0, top: 22, rot: -8 },
    { left: 125, top: 6, rot: 0, front: true },
    { left: 250, top: 22, rot: 8 },
  ],
};

function Backdrop({ photos, price, eyebrow, sub, cta }) {
  // Blur the front card's photo; it changes with the store's sarees.
  const bgPhoto = photoOf(photos[1] || photos[0]);
  const slots = FAN[photos.length];
  return (
    <div className="relative flex items-center gap-[20px] h-[300px] px-[40px] bg-[#2a2320] overflow-hidden">
      <div
        className="absolute inset-[-40px] bg-cover"
        style={{
          backgroundImage: `url("${ikSrc(bgPhoto, [{ width: 600, height: 800 }])}")`,
          backgroundPosition: "center 45%",
          filter: "blur(22px) saturate(1.5) brightness(0.7)",
          transform: "scale(1.3)",
        }}
        aria-hidden="true"
      />
      <div
        className="absolute inset-0"
        style={{
          background:
            "linear-gradient(90deg, rgba(20,16,14,0.85) 0%, rgba(20,16,14,0.45) 55%, rgba(20,16,14,0.1) 100%)",
        }}
        aria-hidden="true"
      />
      <div className="relative flex-1 min-w-0 flex flex-col gap-[12px] text-white">
        <span className="text-[12px] tracking-[0.14em] font-[500] text-[#FFD2BF]">{eyebrow}</span>
        <Heading price={price} className="text-[40px] leading-[1.1]" priceClassName="text-[#FFD2BF]" />
        <span className="text-[15px] text-white/85">{sub}</span>
        <span className="mt-[6px] inline-flex items-center gap-[6px] w-max rounded-full px-[20px] py-[10px] text-[14px] font-[500] bg-white text-ink-900">
          {cta} <Arrow />
        </span>
      </div>
      <div className="relative flex-none w-[420px] h-[260px]" aria-hidden="true">
        {photos.map((p, i) => {
          const s = slots[i];
          return (
            <div
              key={p.skuId || p.productId}
              className={`absolute w-[170px] h-[226px] rounded-[12px] overflow-hidden border-[3px] border-white shadow-[0_14px_30px_rgba(0,0,0,0.4)] ${
                s.front ? "z-[2]" : ""
              }`}
              style={{ left: s.left, top: s.top, transform: `rotate(${s.rot}deg)` }}
            >
              <Photo p={p} />
              {/* The front card covers the last card's bottom-left corner, so its tag sits right. */}
              <PriceTag
                p={p}
                className={photos.length === 3 && i === 2 ? "right-[8px]" : "left-[8px]"}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
}

const DESIGN_COMPONENTS = { warm: Warm, arches: Arches, backdrop: Backdrop };

/**
 * @param {"warm"|"arches"|"backdrop"} design
 * @param {Array} photos - 1 to 3 in-stock products from the banner's listing call
 */
export default function SareesUnderBannerDesktop({ design, ...props }) {
  const Design = DESIGN_COMPONENTS[design] || Warm;
  return (
    <div className="hidden xl:block" data-design={design}>
      <Design {...props} />
    </div>
  );
}
